import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, combineLatest, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { Navigation } from 'app/core/navigation/navigation.types';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { UserService } from 'app/core/user/user.service';
import { LanguageService } from 'app/core/i18n/language.service';

@Component({
    selector     : 'centered-layout',
    templateUrl  : './centered.component.html',
    encapsulation: ViewEncapsulation.None
})
export class CenteredLayoutComponent implements OnInit, OnDestroy
{
    navigation: Navigation;
    isScreenSmall: boolean;
    isLoggedIn: boolean = false;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    // Store raw navigation for re-translation on language change
    private _rawNavigation: Navigation;

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _userService: UserService,
        private _translateService: TranslateService,
        private _languageService: LanguageService
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number
    {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to navigation data and language changes together
        combineLatest([
            this._navigationService.navigation$,
            this._translateService.onLangChange
        ])
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(([navigation, _langEvent]) => {
                this._rawNavigation = navigation;
                this.navigation = this._translateNavigation(navigation);
            });

        // Also handle initial load (before any language change event)
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this._rawNavigation = navigation;
                this.navigation = this._translateNavigation(navigation);
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({matchingAliases}) => {

                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });

        // Subscribe to user changes
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(user => {
                this.isLoggedIn = !!user;
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void
    {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if ( navigation )
        {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    /**
     * Translate navigation items
     * Transforms translation keys (e.g., 'nav.profile') into translated text
     */
    private _translateNavigation(navigation: Navigation): Navigation {
        const translateItems = (items: FuseNavigationItem[]): FuseNavigationItem[] => {
            return items.map(item => ({
                ...item,
                title: this._translateService.instant(item.title),
                children: item.children ? translateItems(item.children) : undefined
            }));
        };

        return {
            compact: translateItems(navigation.compact || []),
            default: translateItems(navigation.default || []),
            futuristic: translateItems(navigation.futuristic || []),
            horizontal: translateItems(navigation.horizontal || [])
        };
    }
}
